import React from 'react';
import { NavLink } from 'react-router-dom';

import styled from 'styled-components/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/pro-regular-svg-icons';
import { FormattedDate } from 'react-intl';

import BlogPanel from '@tovia/man-ui/lib/components/Blog/BlogPanel';
import Badge from '@tovia/man-ui/lib/components/Badge/Badge';
import Badges from '@tovia/man-ui/lib/components/Badge/Badges';

import { formatDateDefaultOptions } from '../../utils/converters';

type Category = {
  UUID: string;
  name: string;
  path: string;
};

type Props = {
  author?: string;
  blogUrl: string;
  categories: Category[];
  className?: string;
  commentCount?: number;
  isSinglePost?: boolean;
  name: string;
  publishedAt: string;
  text: string;
  truncated?: boolean;
};

export const PostItem = ({
  author = '',
  blogUrl,
  categories,
  className = '',
  commentCount = 0,
  isSinglePost,
  name,
  publishedAt,
  text,
  truncated = false,
}: Props) => {
  const commentBadgeText =
    commentCount === 0 ? 'Be the first to comment' : `${commentCount} ${commentCount === 1 ? 'Comment' : 'Comments'}`;

  return (
    <CustomBlogPanel className={className}>
      <div>
        <BlogTitle as={isSinglePost ? 'h1' : 'h2'}>
          <NavLink to={blogUrl}>{name}</NavLink>
        </BlogTitle>
        <Date>
          <FormattedDate {...formatDateDefaultOptions} month="short" value={publishedAt} />
          {!!author && ` - by: ${author}`}
        </Date>
      </div>

      <Content truncated={truncated} dangerouslySetInnerHTML={{ __html: text }} />

      {truncated && (
        <ReadMore>
          <NavLink to={blogUrl} className="btn-read-more btn btn-default clickable">
            READ MORE
          </NavLink>
        </ReadMore>
      )}

      <FooterRow>
        <Badges>
          <Badge
            text={commentBadgeText}
            icon={<FontAwesomeIcon icon={faComments} />}
            url={`${blogUrl}/#comment-section`}
          />
        </Badges>

        {categories.length > 0 && (
          <CategoryList>
            <ul className="list-unstyled list-inline categories">
              <li>Categories</li>
              {categories.map((category) => (
                <li key={category.name}>
                  <NavLink className="tag" to={category.path}>
                    {category.name}
                  </NavLink>
                </li>
              ))}
            </ul>
          </CategoryList>
        )}
      </FooterRow>
    </CustomBlogPanel>
  );
};

const FooterRow = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 5px;
`;

const Date = styled.span`
  display: block;
  font-size: 0.9rem;
  text-transform: capitalize;
`;

const CategoryList = styled.div`
  font-size: 0.9rem;

  @media (min-width: 410px) {
    margin-left: 14px;
  }

  & ul {
    margin-bottom: 0;

    > li {
      vertical-align: middle;
    }
  }
`;

const ReadMore = styled.div`
  position: relative;
  text-align: center;

  ::before {
    content: '';
    height: 45px;
    position: absolute;
    right: 0;
    left: 0;
    top: -45px;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0) 0% 0%,
      ${(props) => props.theme.primary1} 80%,
      ${(props) => props.theme.primary1} 100%
    );
  }
`;

const Content = styled.div<{ truncated?: boolean }>`
  height: auto;
  font-size: 1.07rem;
  padding-bottom: 12px;
  line-height: 1.5em;
  margin-top: 0;
  margin-bottom: 0;

  img {
    display: block;
    max-width: 100%;
    height: auto !important;

    @media (max-width: 740px) {
      width: 100%;
      max-height: 60vw;
      object-fit: contain;
    }
  }

  a {
    color: ${(props) => props.theme.primary9 || 'inherit'};
    text-decoration: underline;
  }

  .samplesbox {
    margin: 8px 0;

    .sample {
      margin: 0 8px;

      img {
        display: inline;
      }
    }
  }

  ${(props) =>
    props.truncated &&
    `
    height: 530px;
    overflow: hidden;
  `}
`;
const CustomBlogPanel = styled(BlogPanel)`
  & .headline {
    margin: 0 0 6px;

    a {
      font-weight: 300;
      text-decoration: none;
      font-size: 1.85rem;

      :focus,
      :hover {
        font-weight: 300;
        text-decoration: none;
        font-size: 1.85rem;
      }
    }
  }

  & .btn-read-more {
    padding: 10px 110px;
    margin: 20px 0;
    background: ${(props) => props.theme.primary1};
    color: ${(props) => props.theme.primary4};
    border-radius: 0;
    border-color: ${(props) => props.theme.primary3};

    @media (max-width: 374px) {
      padding: 10px 80px;
    }
  }

  & .comment-link {
    font-size: 0.9rem;

    :hover {
      font-size: 0.9rem;
      text-decoration: none;
    }
  }

  & .btn-share {
    text-indent: 15px;
    margin-left: 10px;
  }

  & .comment-text {
    padding-left: 5px;
    vertical-align: text-top;
  }
`;

const BlogTitle = styled.h2`
  font-size: 1.85rem;
  font-weight: 300;
  margin: 0 0 6px;

  a {
    color: ${(props) => props.theme.primary9 || 'inherit'};
    text-decoration: none;

    :focus,
    :hover {
      color: ${(props) => props.theme.primary9 || 'inherit'};
      text-decoration: none;
    }
  }
`;
